/**
 * Helper functions for attendee data extraction
 */

/**
 * Get attendee's full name from their data
 * @param {Object} attendee - Attendee object with data array
 * @returns {string} - Full name or 'N/A'
 */
export const getAttendeeName = (attendee) => {
  if (!attendee) {
    return 'N/A';
  }

  // First check for direct full_name property (most common)
  if (attendee.full_name) {
    return attendee.full_name;
  }

  // Check if data is in the new format (fields object)
  if (attendee.fields && typeof attendee.fields === 'object') {
    // New format: attendee.fields.full_name?.value
    if (attendee.fields.full_name?.value) {
      return attendee.fields.full_name.value;
    }

    if (attendee.fields.name?.value) {
      return attendee.fields.name.value;
    }

    // Look for any field with 'name' in the label
    for (const [, fieldData] of Object.entries(attendee.fields)) {
      if (fieldData?.label?.toLowerCase().includes('name') && fieldData.value) {
        return fieldData.value;
      }
    }

    // Return first field value as fallback
    const firstField = Object.values(attendee.fields)[0];
    return firstField?.value || 'N/A';
  }

  // Original format: attendee.data array
  if (attendee.data && Array.isArray(attendee.data)) {
    // Try to find by field_name first (most reliable)
    const nameByFieldName = attendee.data.find(d =>
      d.field?.field_name === 'full_name' ||
      d.field?.field_name === 'name'
    );

    if (nameByFieldName?.value) {
      return nameByFieldName.value;
    }
    if (nameByFieldName?.field_value) {
      return nameByFieldName.field_value;
    }

    // Try to find by field_label containing 'name'
    const nameByLabel = attendee.data.find(d =>
      d.field?.field_label?.toLowerCase().includes('name')
    );

    if (nameByLabel?.value) {
      return nameByLabel.value;
    }
    if (nameByLabel?.field_value) {
      return nameByLabel.field_value;
    }

    // Fallback: return first text field value
    const firstTextField = attendee.data.find(d =>
      d.field?.field_type === 'text' && (d.value || d.field_value)
    );

    return firstTextField?.value || firstTextField?.field_value || 'N/A';
  }

  return 'N/A';
};

/**
 * Get attendee's email from their data
 * @param {Object} attendee - Attendee object with data array
 * @returns {string} - Email or 'N/A'
 */
export const getAttendeeEmail = (attendee) => {
  if (!attendee) {
    return 'N/A';
  }

  // First check for direct email property
  if (attendee.email) {
    return attendee.email;
  }

  if (!attendee.data || !Array.isArray(attendee.data)) {
    return 'N/A';
  }

  const emailField = attendee.data.find(d =>
    d.field?.field_type === 'email' ||
    d.field?.field_name === 'email'
  );

  return emailField?.value || emailField?.field_value || 'N/A';
};

/**
 * Get attendee's phone from their data
 * @param {Object} attendee - Attendee object with data array
 * @returns {string} - Phone or 'N/A'
 */
export const getAttendeePhone = (attendee) => {
  if (!attendee) {
    return 'N/A';
  }

  // First check for direct phone property
  if (attendee.phone) {
    return attendee.phone;
  }

  if (!attendee.data || !Array.isArray(attendee.data)) {
    return 'N/A';
  }

  const phoneField = attendee.data.find(d =>
    d.field?.field_type === 'phone' ||
    d.field?.field_name === 'phone'
  );

  return phoneField?.value || phoneField?.field_value || 'N/A';
};

/**
 * Get attendee's company from their data
 * @param {Object} attendee - Attendee object with data array
 * @returns {string} - Company or 'N/A'
 */
export const getAttendeeCompany = (attendee) => {
  if (!attendee) {
    return 'N/A';
  }

  // First check for direct organization property
  if (attendee.organization) {
    return attendee.organization;
  }

  if (!attendee.data || !Array.isArray(attendee.data)) {
    return 'N/A';
  }

  const companyField = attendee.data.find(d =>
    d.field?.field_name === 'company' ||
    d.field?.field_name === 'organization' ||
    d.field?.field_label?.toLowerCase().includes('company') ||
    d.field?.field_label?.toLowerCase().includes('organization')
  );

  return companyField?.value || companyField?.field_value || 'N/A';
};

/**
 * Get field value by field name
 * @param {Object} attendee - Attendee object with data array
 * @param {string} fieldName - Field name to search for
 * @returns {string} - Field value or 'N/A'
 */
export const getAttendeeFieldValue = (attendee, fieldName) => {
  if (!attendee?.data || !Array.isArray(attendee.data)) {
    return 'N/A';
  }

  const field = attendee.data.find(d =>
    d.field?.field_name === fieldName
  );

  return field?.value || field?.field_value || 'N/A';
};

/**
 * Debug function to see all attendee data
 * @param {Object} attendee - Attendee object with data array
 * @returns {string} - Debug info
 */
export const debugAttendeeData = (attendee) => {
  if (!attendee?.data || !Array.isArray(attendee.data)) {
    return 'No data array';
  }

  return attendee.data.map(d => ({
    field_name: d.field?.field_name,
    field_label: d.field?.field_label,
    field_type: d.field?.field_type,
    value: d.value,
    field_value: d.field_value,
    field_id: d.field?.id,
  }));
};
